import { MobileSelect } from '@/components/ui';
import { SupplierSelect } from './SupplierSelect';
import type { ReceiptFile } from '../types';

interface ReceiptPreviewCardProps {
  receipt: ReceiptFile;
  onRemove: (id: string) => void;
  onProjectChange?: (id: string, projectId: string | null) => void;
  onSupplierChange?: (id: string, supplierId: string | null) => void;
  projects?: Array<{ id: string; name: string }>;
  showProjectSelect?: boolean;
  showSupplierSelect?: boolean;
}

export function ReceiptPreviewCard({
  receipt,
  onRemove,
  onProjectChange,
  onSupplierChange,
  projects = [],
  showProjectSelect = true,
  showSupplierSelect = true,
}: ReceiptPreviewCardProps) {
  const statusColors = {
    pending: 'bg-secondary-100 text-secondary-600',
    ready: 'bg-green-100 text-green-600',
    uploading: 'bg-yellow-100 text-yellow-600',
    uploaded: 'bg-green-100 text-green-600',
    error: 'bg-red-100 text-red-600',
  };

  const statusLabels = {
    pending: 'Pending',
    ready: 'Ready',
    uploading: 'Uploading...',
    uploaded: 'Uploaded',
    error: 'Error',
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="bg-white rounded-lg border border-secondary-200 overflow-visible">
      {/* Preview */}
      <div className="relative aspect-square bg-secondary-100 rounded-t-lg overflow-hidden">
        {receipt.isPdf ? (
          // PDF icon placeholder
          <div className="w-full h-full flex flex-col items-center justify-center text-secondary-400">
            <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 13h2a1.5 1.5 0 0 1 0 3H9v2H8v-5h.5zm2 2H9v-1h1.5a.5.5 0 0 1 0 1zM14 13h1.5a1.5 1.5 0 0 1 1.5 1.5v2a1.5 1.5 0 0 1-1.5 1.5H14v-5zm1 4h.5a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5H15v3zm-9 0h2v1H5v-5h1v4z" />
            </svg>
            <span className="text-sm mt-2">PDF Document</span>
          </div>
        ) : receipt.preview ? (
          <img
            src={receipt.preview}
            alt={receipt.file.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-secondary-400">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}

        {/* Remove Button */}
        <button
          onClick={() => onRemove(receipt.id)}
          className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
          disabled={receipt.status === 'uploading'}
        >
          <svg className="w-4 h-4 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Status Badge */}
        <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${statusColors[receipt.status]}`}>
          {statusLabels[receipt.status]}
        </div>

        {/* Upload Progress */}
        {receipt.status === 'uploading' && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-secondary-200">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{ width: `${receipt.uploadProgress || 0}%` }}
            />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 space-y-2">
        {/* File Name */}
        <p className="text-sm font-medium text-secondary-900 truncate" title={receipt.file.name}>
          {receipt.file.name}
        </p>

        {/* File Size */}
        <p className="text-xs text-secondary-500">
          {formatFileSize(receipt.file.size)}
        </p>

        {/* Error Message */}
        {receipt.status === 'error' && receipt.error && (
          <p className="text-xs text-red-600">{receipt.error}</p>
        )}
      </div>

      {/* Selectors - Outside the overflow hidden container */}
      {receipt.status === 'ready' && (showProjectSelect || showSupplierSelect) && (
        <div className="px-3 pb-3 space-y-2">
          {/* Project Select */}
          {showProjectSelect && onProjectChange && (
            <MobileSelect
              label="Project"
              value={receipt.selectedProjectId || ''}
              onChange={(value) => onProjectChange(receipt.id, value || null)}
              placeholder="No project (unassigned)"
              options={[
                { value: '', label: 'No project (unassigned)' },
                ...projects.map((project) => ({
                  value: project.id,
                  label: project.name,
                })),
              ]}
            />
          )}

          {/* Supplier Select */}
          {showSupplierSelect && onSupplierChange && (
            <SupplierSelect
              value={receipt.selectedSupplierId || null}
              onChange={(supplierId) => onSupplierChange(receipt.id, supplierId)}
            />
          )}
        </div>
      )}
    </div>
  );
}